(() => {
  'use strict';

  const runtimeRoot = document.documentElement;
  if (runtimeRoot.dataset.plRuntimeReady === 'true') return;
  runtimeRoot.dataset.plRuntimeReady = 'true';
  runtimeRoot.classList.add('pl-js');

  const mountedSections = new WeakMap();
  const mountedCarousels = new WeakSet();
  const mountedFullBanners = new WeakSet();
  const mountedVideoGalleries = new WeakSet();
  const mountedSpotlights = new WeakSet();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const headerSelector = '.shopify-section-group-header-group,[id^="shopify-section-sections--"][id$="__header"],#shopify-section-header,header';
  let headerResizeObserver = null;
  let headerOffsetFrame = 0;
  let headerOffsetBound = false;

  const modulo = (value, length) => ((value % length) + length) % length;

  function bindSpotlight(card, signal) {
    if (mountedSpotlights.has(card)) return;
    mountedSpotlights.add(card);

    let frame = 0;
    let nextX = 0;
    let nextY = 0;
    const paint = () => {
      frame = 0;
      card.style.setProperty('--mouse-x', `${nextX}px`);
      card.style.setProperty('--mouse-y', `${nextY}px`);
    };

    card.addEventListener('pointermove', (event) => {
      const rect = card.getBoundingClientRect();
      nextX = event.clientX - rect.left;
      nextY = event.clientY - rect.top;
      card.classList.add('is-spotlit');
      if (!frame) frame = requestAnimationFrame(paint);
    }, { passive: true, signal });

    card.addEventListener('pointerleave', () => {
      card.classList.remove('is-spotlit');
    }, { passive: true, signal });
  }

  function bindAllSpotlights(root, signal) {
    root.querySelectorAll('[data-pl-spotlight]').forEach((card) => bindSpotlight(card, signal));
  }

  function setupLightbox(carousel, signal, onClose) {
    const dialog = carousel.querySelector('[data-pl-lightbox]');
    const content = dialog?.querySelector('[data-pl-lightbox-content]');
    if (!dialog || !content) return;
    let opener = null;

    const clear = () => {
      content.querySelectorAll('video').forEach((video) => video.pause());
      content.replaceChildren();
      if (typeof onClose === 'function') onClose();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      opener = null;
    };

    const dismiss = () => {
      if (dialog.open) dialog.close();
      else clear();
    };

    const close = () => {
      if (document.fullscreenElement === dialog && typeof document.exitFullscreen === 'function') {
        const exitPromise = document.exitFullscreen();
        if (exitPromise && typeof exitPromise.finally === 'function') exitPromise.catch(() => {}).finally(dismiss);
        else dismiss();
        return;
      }
      dismiss();
    };

    carousel.addEventListener('click', (event) => {
      const openButton = event.target.closest('[data-pl-media-open]');
      if (openButton && carousel.contains(openButton)) {
        const slide = openButton.closest('[data-pl-slide]');
        const source = slide?.querySelector('.pl-media-slide__media :is(video,img,svg,iframe)');
        if (!source) return;
        opener = openButton;
        carousel.querySelectorAll('video').forEach((video) => video.pause());
        const preview = source.cloneNode(true);
        preview.removeAttribute('id');
        if (preview instanceof HTMLVideoElement) {
          preview.controls = true;
          preview.autoplay = true;
          preview.playsInline = true;
        } else if (preview instanceof HTMLIFrameElement) {
          preview.loading = 'eager';
          preview.allowFullscreen = true;
          preview.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
        }
        content.replaceChildren(preview);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        if (typeof dialog.requestFullscreen === 'function' && document.fullscreenElement !== dialog) {
          const fullscreenPromise = dialog.requestFullscreen({ navigationUI: 'hide' });
          if (fullscreenPromise && typeof fullscreenPromise.catch === 'function') fullscreenPromise.catch(() => {});
        }
        if (preview instanceof HTMLVideoElement) {
          const promise = preview.play();
          if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        }
        return;
      }
      const closeButton = event.target.closest('[data-pl-lightbox-close]');
      if (closeButton && dialog.contains(closeButton)) close();
    }, { signal });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    }, { signal });
    dialog.addEventListener('close', clear, { signal });
  }

  function setupCarousel(carousel, signal) {
    if (mountedCarousels.has(carousel)) return;
    mountedCarousels.add(carousel);

    const slides = Array.from(carousel.querySelectorAll('[data-pl-slide]'));
    const tabs = Array.from(carousel.querySelectorAll('[data-pl-tab]'));
    const stage = carousel.querySelector('[data-pl-stage]');
    if (!slides.length || !stage) return;

    let active = Math.min(Number(carousel.dataset.plInitial || 0), slides.length - 1);
    let touchX = null;
    let editorPaused = false;

    const resolveTikTokSource = async (shell) => {
      const originalUrl = shell?.dataset.plResolveUrl || '';
      if (!originalUrl) return '';
      let decodedUrl = originalUrl;
      try { decodedUrl = decodeURIComponent(originalUrl); } catch (_) {}
      let videoId = decodedUrl.match(/(?:\/video\/|\/player\/v1\/|[?&](?:video_id|item_id)=)(\d+)/i)?.[1] || '';
      if (!videoId) {
        const endpoint = `https://www.tiktok.com/oembed?url=${encodeURIComponent(originalUrl)}`;
        const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`TikTok oEmbed ${response.status}`);
        const payload = await response.json();
        const embedMarkup = String(payload?.html || '');
        videoId = embedMarkup.match(/data-video-id=["'](\d+)/i)?.[1]
          || embedMarkup.match(/\/video\/(\d+)/i)?.[1]
          || '';
      }
      if (!videoId) throw new Error('TikTok video ID unavailable');
      return `https://www.tiktok.com/player/v1/${videoId}?controls=1&progress_bar=1&play_button=1&volume_control=1&fullscreen_button=1&rel=0`;
    };

    const mountExternalVideo = async (shell) => {
      if (!shell || shell.dataset.plMounted === 'true') return shell?.querySelector('iframe') || null;
      if (shell.dataset.plResolving === 'true') return null;
      let src = shell.dataset.plSrc || '';
      const loadButton = shell.querySelector('[data-pl-external-load]');
      const loadLabel = loadButton?.querySelector('span:last-child');
      const originalLabel = loadButton?.dataset.plLabel || loadLabel?.textContent || 'Play video';
      if (!src && shell.dataset.plProvider === 'tiktok' && shell.dataset.plResolveUrl) {
        shell.dataset.plResolving = 'true';
        if (loadButton) loadButton.disabled = true;
        if (loadLabel) loadLabel.textContent = 'Loading TikTok…';
        try {
          src = await resolveTikTokSource(shell);
          shell.dataset.plSrc = src;
        } catch (_) {
          shell.classList.add('is-error');
          if (loadLabel) loadLabel.textContent = 'Unable to load. Please enter the video ID.';
          if (loadButton) loadButton.disabled = false;
          delete shell.dataset.plResolving;
          return null;
        }
        delete shell.dataset.plResolving;
      }
      if (!src) return null;
      if (typeof shell.plInitialMarkup !== 'string') shell.plInitialMarkup = shell.innerHTML;
      const openLink = shell.querySelector('.pl-media-slide__open');
      const iframe = document.createElement('iframe');
      iframe.className = 'pl-media-slide__external';
      iframe.dataset.plExternalProvider = shell.dataset.plProvider || '';
      iframe.src = `${src}${src.includes('?') ? '&' : '?'}autoplay=1`;
      iframe.title = shell.dataset.plTitle || 'External video';
      iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      shell.replaceChildren(iframe);
      if (openLink) shell.append(openLink);
      shell.dataset.plMounted = 'true';
      shell.classList.remove('is-error');
      if (loadLabel) loadLabel.textContent = originalLabel;
      return iframe;
    };

    const setSlideFocusable = (slide, enabled) => {
      const selector = 'a[href],button,input,select,textarea,video[controls],[tabindex]';
      slide.querySelectorAll(selector).forEach((control) => {
        if (!control.hasAttribute('data-pl-original-tabindex')) {
          control.dataset.plOriginalTabindex = control.getAttribute('tabindex') ?? '';
        }
        if (!enabled) {
          control.setAttribute('tabindex', '-1');
          return;
        }
        const original = control.dataset.plOriginalTabindex;
        if (original === '') control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', original);
      });
    };

    const syncVideos = () => {
      slides.forEach((slide, index) => {
        slide.querySelectorAll('video').forEach((video) => {
          if (index === active && carousel.dataset.plAutoplay === 'true' && !editorPaused && !reduceMotion.matches) {
            const promise = video.play();
            if (promise && typeof promise.catch === 'function') promise.catch(() => {});
          } else {
            video.pause();
            if (index !== active) video.currentTime = 0;
          }
        });
      });
      slides.forEach((slide, index) => slide.querySelectorAll('[data-pl-external-shell][data-pl-provider="instagram"]').forEach((shell) => {
        if (index !== active && shell.dataset.plMounted === 'true' && typeof shell.plInitialMarkup === 'string') {
          shell.innerHTML = shell.plInitialMarkup;
          delete shell.dataset.plMounted;
        }
      }));
      slides.forEach((slide, index) => slide.querySelectorAll('[data-pl-external-provider]').forEach((frame) => {
        if (!frame.contentWindow) return;
        const shouldPlay = index === active && carousel.dataset.plAutoplay === 'true' && !editorPaused && !reduceMotion.matches;
        if (frame.dataset.plExternalProvider === 'youtube') {
          frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: shouldPlay ? 'playVideo' : 'pauseVideo', args: [] }), '*');
        } else if (frame.dataset.plExternalProvider === 'vimeo') {
          frame.contentWindow.postMessage({ method: shouldPlay ? 'play' : 'pause' }, '*');
        } else if (frame.dataset.plExternalProvider === 'tiktok') {
          frame.contentWindow.postMessage({ type: shouldPlay ? 'play' : 'pause', value: null, 'x-tiktok-player': true }, '*');
        }
      }));
    };

    setupLightbox(carousel, signal, syncVideos);

    const goTo = (index, focusTab = false) => {
      active = modulo(index, slides.length);
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === active);
        slide.classList.toggle('is-prev', slideIndex === modulo(active - 1, slides.length));
        slide.classList.toggle('is-next', slideIndex === modulo(active + 1, slides.length));
        slide.setAttribute('aria-hidden', slideIndex === active ? 'false' : 'true');
        slide.tabIndex = slideIndex === active ? 0 : -1;
        setSlideFocusable(slide, slideIndex === active);
      });
      tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === active;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
      carousel.dataset.plActive = String(active);
      syncVideos();
      if (focusTab && tabs[active]) tabs[active].focus({ preventScroll: true });
    };

    carousel.addEventListener('click', (event) => {
      const loadButton = event.target.closest('[data-pl-external-load]');
      if (loadButton && carousel.contains(loadButton)) {
        mountExternalVideo(loadButton.closest('[data-pl-external-shell]'));
        return;
      }
      const trigger = event.target.closest('[data-pl-action],[data-pl-index]');
      if (!trigger || !carousel.contains(trigger)) return;
      if (trigger.dataset.plAction === 'prev') goTo(active - 1);
      if (trigger.dataset.plAction === 'next') goTo(active + 1);
      if (trigger.dataset.plIndex !== undefined) {
        const targetIndex = Number(trigger.dataset.plIndex);
        if (targetIndex !== active) event.preventDefault();
        goTo(targetIndex);
      }
    }, { signal });

    carousel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(active - 1, true);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(active + 1, true);
      }
    }, { signal });

    stage.addEventListener('touchstart', (event) => {
      touchX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true, signal });

    stage.addEventListener('touchend', (event) => {
      if (touchX === null) return;
      const endX = event.changedTouches[0]?.clientX ?? touchX;
      const distance = endX - touchX;
      touchX = null;
      if (Math.abs(distance) < 42) return;
      goTo(active + (distance < 0 ? 1 : -1));
    }, { passive: true, signal });

    carousel.plGoTo = goTo;
    carousel.plEditorSelect = (index) => {
      editorPaused = true;
      carousel.dataset.plEditorPaused = 'true';
      goTo(index);
    };
    carousel.plEditorDeselect = () => {
      editorPaused = false;
      carousel.dataset.plEditorPaused = 'false';
      syncVideos();
    };
    carousel.dataset.plEditorPaused = 'false';
    goTo(active);
  }

  function setupFullBanner(banner, signal) {
    if (mountedFullBanners.has(banner)) return;
    mountedFullBanners.add(banner);

    const slides = Array.from(banner.querySelectorAll('[data-pl-banner-slide]'));
    const dots = Array.from(banner.querySelectorAll('[data-pl-banner-index]'));
    if (!slides.length) {
      mountedFullBanners.delete(banner);
      return;
    }

    let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let wheelLocked = false;
    let interval = 0;
    const autoplayMedia = banner.dataset.plAutoplayMedia === 'true';
    const hoverMedia = banner.dataset.plHoverMedia === 'true';

    const syncMedia = () => {
      slides.forEach((slide, index) => slide.querySelectorAll('.pl-full-banner__video').forEach((video) => {
        if (index === active && autoplayMedia && !reduceMotion.matches) {
          const promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        } else {
          video.pause();
          if (index !== active) video.currentTime = 0;
        }
      }));
    };

    const goTo = (index) => {
      active = modulo(index, slides.length);
      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === active;
        slide.classList.toggle('is-active', selected);
        slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });
      dots.forEach((dot, dotIndex) => {
        const selected = dotIndex === active;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
      const desktopHeight = Number(slides[active]?.dataset.plSlideHeight || 0);
      const mobileHeight = Number(slides[active]?.dataset.plSlideHeightMobile || 0);
      banner.style.setProperty('--pl-banner-active-height', desktopHeight > 0 ? `${desktopHeight}px` : 'var(--pl-banner-height)');
      banner.style.setProperty('--pl-banner-active-height-mobile', mobileHeight > 0 ? `${mobileHeight}px` : 'var(--pl-banner-height-mobile)');
      syncMedia();
    };

    banner.addEventListener('click', (event) => {
      const action = event.target.closest('[data-pl-banner-action]');
      const dot = event.target.closest('[data-pl-banner-index]');
      if (action) goTo(active + (action.dataset.plBannerAction === 'next' ? 1 : -1));
      if (dot) goTo(Number(dot.dataset.plBannerIndex));
    }, { signal });

    banner.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goTo(active + (event.key === 'ArrowRight' ? 1 : -1));
    }, { signal });

    if (banner.dataset.plWheel === 'true' && slides.length > 1) {
      banner.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaY) < 18) return;
        event.preventDefault();
        if (wheelLocked) return;
        wheelLocked = true;
        goTo(active + (event.deltaY > 0 ? 1 : -1));
        window.setTimeout(() => { wheelLocked = false; }, 520);
      }, { passive: false, signal });
    }

    if (!autoplayMedia && hoverMedia) {
      banner.addEventListener('pointerenter', () => {
        slides[active]?.querySelectorAll('.pl-full-banner__video').forEach((video) => {
          if (reduceMotion.matches) return;
          const promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        });
      }, { signal });
      banner.addEventListener('pointerleave', () => {
        slides[active]?.querySelectorAll('.pl-full-banner__video').forEach((video) => video.pause());
      }, { signal });
    }

    if (banner.dataset.plAutoplaySlides === 'true' && slides.length > 1 && !reduceMotion.matches) {
      const delay = Math.max(3000, Number(banner.dataset.plInterval || 6000));
      const start = () => {
        if (!interval) interval = window.setInterval(() => goTo(active + 1), delay);
      };
      const stop = () => {
        if (interval) window.clearInterval(interval);
        interval = 0;
      };
      banner.addEventListener('pointerenter', stop, { signal });
      banner.addEventListener('pointerleave', start, { signal });
      banner.addEventListener('focusin', stop, { signal });
      banner.addEventListener('focusout', start, { signal });
      signal.addEventListener('abort', stop, { once: true });
      start();
    }

    banner.plBannerGoTo = goTo;
    goTo(active);
  }

  function setupVideoGallery(gallery, signal) {
    if (mountedVideoGalleries.has(gallery)) return;
    mountedVideoGalleries.add(gallery);

    const panels = Array.from(gallery.querySelectorAll('[data-pl-video-panel]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-pl-video-index]'));
    if (!panels.length) return;
    let active = 0;

    const mountExternalVideo = (shell) => {
      if (!shell || shell.dataset.plMounted === 'true') return shell && shell.querySelector('iframe');
      const src = shell.dataset.plSrc;
      if (!src) return null;
      if (typeof shell.plInitialMarkup !== 'string') shell.plInitialMarkup = shell.innerHTML;
      const openLink = shell.querySelector('.pl-video-gallery__open');
      const iframe = document.createElement('iframe');
      iframe.className = 'pl-video-gallery__external';
      iframe.dataset.plExternalProvider = shell.dataset.plProvider || '';
      iframe.src = `${src}${src.includes('?') ? '&' : '?'}autoplay=1`;
      iframe.title = shell.dataset.plTitle || 'External video';
      iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      shell.replaceChildren(iframe);
      if (openLink) shell.append(openLink);
      shell.dataset.plMounted = 'true';
      return iframe;
    };

    const syncPlayback = () => {
      panels.forEach((panel, index) => panel.querySelectorAll('video').forEach((video) => {
        if (index === active && gallery.dataset.plAutoplay === 'true' && !reduceMotion.matches) {
          const promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(() => {});
        } else {
          video.pause();
        }
      }));
      panels.forEach((panel, index) => panel.querySelectorAll('[data-pl-external-shell][data-pl-provider="instagram"]').forEach((shell) => {
        if (index !== active && shell.dataset.plMounted === 'true' && typeof shell.plInitialMarkup === 'string') {
          shell.innerHTML = shell.plInitialMarkup;
          delete shell.dataset.plMounted;
        }
      }));
      panels.forEach((panel, index) => panel.querySelectorAll('[data-pl-external-provider]').forEach((frame) => {
        if (!frame.contentWindow) return;
        const shouldPlay = index === active && gallery.dataset.plAutoplay === 'true' && !reduceMotion.matches;
        if (frame.dataset.plExternalProvider === 'youtube') {
          frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: shouldPlay ? 'playVideo' : 'pauseVideo', args: [] }), '*');
        } else if (frame.dataset.plExternalProvider === 'vimeo') {
          frame.contentWindow.postMessage({ method: shouldPlay ? 'play' : 'pause' }, '*');
        } else if (frame.dataset.plExternalProvider === 'tiktok') {
          frame.contentWindow.postMessage({ type: shouldPlay ? 'play' : 'pause', value: null, 'x-tiktok-player': true }, '*');
        } else if (frame.dataset.plExternalProvider === 'instagram') {
          const originalSrc = frame.dataset.plOriginalSrc || frame.src;
          frame.dataset.plOriginalSrc = originalSrc;
          if (index === active && frame.src === 'about:blank') frame.src = originalSrc;
          if (index !== active && frame.src !== 'about:blank') frame.src = 'about:blank';
        }
      }));
    };

    const goTo = (index, focusThumb = false) => {
      active = modulo(index, panels.length);
      panels.forEach((panel, panelIndex) => {
        const selected = panelIndex === active;
        panel.classList.toggle('is-active', selected);
        panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });
      thumbs.forEach((thumb, thumbIndex) => {
        const selected = thumbIndex === active;
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-selected', selected ? 'true' : 'false');
        thumb.tabIndex = selected ? 0 : -1;
      });
      syncPlayback();
      if (focusThumb && thumbs[active]) thumbs[active].focus({ preventScroll: true });
    };

    gallery.addEventListener('click', (event) => {
      const loadButton = event.target.closest('[data-pl-external-load]');
      if (loadButton && gallery.contains(loadButton)) {
        mountExternalVideo(loadButton.closest('[data-pl-external-shell]'));
        return;
      }
      const thumb = event.target.closest('[data-pl-video-index]');
      if (thumb && gallery.contains(thumb)) goTo(Number(thumb.dataset.plVideoIndex));
    }, { signal });
    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goTo(active + (event.key === 'ArrowRight' ? 1 : -1), true);
    }, { signal });

    gallery.plVideoGoTo = goTo;
    goTo(0);
  }

  function mount(section) {
    if (!section || mountedSections.has(section)) return;
    const controller = new AbortController();
    const { signal } = controller;
    bindAllSpotlights(section, signal);
    section.querySelectorAll('[data-pl-carousel]').forEach((carousel) => setupCarousel(carousel, signal));
    section.querySelectorAll('[data-pl-full-banner]').forEach((banner) => setupFullBanner(banner, signal));
    section.querySelectorAll('[data-pl-video-gallery]').forEach((gallery) => setupVideoGallery(gallery, signal));

    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('[data-pl-spotlight]')) bindSpotlight(node, signal);
        bindAllSpotlights(node, signal);
        if (node.matches('[data-pl-carousel]')) setupCarousel(node, signal);
        node.querySelectorAll('[data-pl-carousel]').forEach((carousel) => setupCarousel(carousel, signal));
        if (node.matches('[data-pl-full-banner]')) setupFullBanner(node, signal);
        node.querySelectorAll('[data-pl-full-banner]').forEach((banner) => setupFullBanner(banner, signal));
        if (node.matches('[data-pl-video-gallery]')) setupVideoGallery(node, signal);
        node.querySelectorAll('[data-pl-video-gallery]').forEach((gallery) => setupVideoGallery(gallery, signal));
      }));
    });
    observer.observe(section, { childList: true, subtree: true });

    mountedSections.set(section, () => {
      observer.disconnect();
      controller.abort();
      section.querySelectorAll('video').forEach((video) => video.pause());
      section.querySelectorAll('[data-pl-lightbox]').forEach((dialog) => {
        if (dialog.open) dialog.close();
        dialog.querySelector('[data-pl-lightbox-content]')?.replaceChildren();
      });
      section.querySelectorAll('[data-pl-spotlight]').forEach((card) => mountedSpotlights.delete(card));
      section.querySelectorAll('[data-pl-carousel]').forEach((carousel) => {
        mountedCarousels.delete(carousel);
        delete carousel.plGoTo;
        delete carousel.plEditorSelect;
        delete carousel.plEditorDeselect;
      });
      section.querySelectorAll('[data-pl-full-banner]').forEach((banner) => {
        mountedFullBanners.delete(banner);
        delete banner.plBannerGoTo;
      });
      section.querySelectorAll('[data-pl-video-gallery]').forEach((gallery) => {
        mountedVideoGalleries.delete(gallery);
        delete gallery.plVideoGoTo;
      });
      mountedSections.delete(section);
    });
  }

  function unmount(section) {
    const cleanup = mountedSections.get(section);
    if (cleanup) cleanup();
  }

  function mountWithin(root) {
    if (root.matches?.('[data-pl-section]')) mount(root);
    root.querySelectorAll?.('[data-pl-section]').forEach(mount);
  }

  function isOverlayHeader(element) {
    let node = element;
    while (node && node !== document.body) {
      const position = getComputedStyle(node).position;
      if (position === 'absolute' || position === 'fixed') return true;
      node = node.parentElement;
    }
    return false;
  }

  function measureHeaderOffset() {
    const firstPage = document.querySelector('.pl-page');
    if (!firstPage) return;

    document.querySelectorAll('.pl-page--first').forEach((page) => page.classList.remove('pl-page--first'));
    firstPage.classList.add('pl-page--first');

    let overlayBottom = 0;
    document.querySelectorAll(headerSelector).forEach((header) => {
      const style = getComputedStyle(header);
      if (style.display === 'none' || style.visibility === 'hidden' || !isOverlayHeader(header)) return;
      const rect = header.getBoundingClientRect();
      if (rect.bottom > 0) overlayBottom = Math.max(overlayBottom, rect.bottom);
    });

    const offset = Math.max(0, Math.ceil(overlayBottom));
    runtimeRoot.style.setProperty('--pl-header-offset', `${offset}px`);
    document.body.classList.toggle('pl-has-overlay-header', offset > 0);
  }

  function scheduleHeaderOffset() {
    if (headerOffsetFrame) cancelAnimationFrame(headerOffsetFrame);
    headerOffsetFrame = requestAnimationFrame(() => {
      headerOffsetFrame = 0;
      measureHeaderOffset();
    });
  }

  function bindHeaderOffset() {
    measureHeaderOffset();
    headerResizeObserver?.disconnect();
    if ('ResizeObserver' in window) {
      headerResizeObserver = new ResizeObserver(scheduleHeaderOffset);
      document.querySelectorAll(headerSelector).forEach((header) => headerResizeObserver.observe(header));
    }
    if (!headerOffsetBound) {
      window.addEventListener('resize', scheduleHeaderOffset, { passive: true });
      headerOffsetBound = true;
    }
  }

  function boot() {
    const page = document.querySelector('.pl-page');
    if (!page) return;
    runtimeRoot.classList.add('pl-js');
    document.body.setAttribute('data-pl-page-active', 'true');
    bindHeaderOffset();
    mountWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    mountWithin(event.target);
    bindHeaderOffset();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.('[data-pl-section]') ? event.target : event.target.querySelector?.('[data-pl-section]');
    if (section) unmount(section);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId || event.target.closest?.('[data-pl-block-id]')?.dataset.plBlockId;
    if (!blockId) return;
    document.querySelectorAll('[data-pl-carousel]').forEach((carousel) => {
      const slides = Array.from(carousel.querySelectorAll('[data-pl-slide]'));
      const index = slides.findIndex((slide) => slide.dataset.plBlockId === blockId);
      if (index >= 0 && typeof carousel.plEditorSelect === 'function') carousel.plEditorSelect(index);
    });
    document.querySelectorAll('[data-pl-full-banner]').forEach((banner) => {
      const slides = Array.from(banner.querySelectorAll('[data-pl-banner-slide]'));
      const index = slides.findIndex((slide) => slide.dataset.plBlockId === blockId);
      if (index >= 0 && typeof banner.plBannerGoTo === 'function') banner.plBannerGoTo(index);
    });
    document.querySelectorAll('[data-pl-video-gallery]').forEach((gallery) => {
      const panels = Array.from(gallery.querySelectorAll('[data-pl-video-panel]'));
      const index = panels.findIndex((panel) => panel.dataset.plBlockId === blockId);
      if (index >= 0 && typeof gallery.plVideoGoTo === 'function') gallery.plVideoGoTo(index);
    });
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const blockId = event.detail?.blockId || event.target.closest?.('[data-pl-block-id]')?.dataset.plBlockId;
    document.querySelectorAll('[data-pl-carousel]').forEach((carousel) => {
      if (!blockId || carousel.querySelector(`[data-pl-block-id="${blockId}"]`)) {
        if (typeof carousel.plEditorDeselect === 'function') carousel.plEditorDeselect();
      }
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
